import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, Portal } from '@ark-ui/react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Alert } from '../components/Alert';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { Icon } from '../icons/Icon';
import { useIdentity, type LoginStatus, type WebDeviceChoice } from '../hooks/useIdentity';
import type { MigrationPromptContext, MigrationPromptResult } from '../hooks/useIdentity.types';
import { useAuth } from '../hooks/useAuth';
import { WebDeviceChoiceModal } from '../components/WebDeviceChoiceModal';
import { MigrationPromptModal } from '../components/MigrationPromptModal';
import { useAgeVerification } from '../hooks/useAgeVerification';
import { useCreateAliasWizard } from '../hooks/useCreateAliasWizard';
import { CreateAliasWizard } from './identity/CreateAliasWizard';
import { MIN_PASSPHRASE_LENGTH } from './identityModalUtils';
import {
  createIdentityErrorI18nKey,
  loginIdentityErrorI18nKey,
  unlockIdentityErrorI18nKey,
} from '../services/identityErrorI18n';
import { createApiClient } from '@adieuu/shared';
import { useAppConfig, usePlatformCapabilities } from '../config';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { UpdateAvailableBanner } from '../components/UpdateAvailableBanner';
import { panicWipeLocalClientData } from '../services/panicWipeLocalClientData';

interface IdentityModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** If true, show unlock view instead of login (for locked sessions after refresh) */
  unlockMode?: boolean;
  /**
   * Hides the unlock dialog without unlocking. Escape and outside clicks stay
   * blocked; only this control minimizes.
   */
  onMinimize?: () => void;
}

type ModalView = 'choose' | 'login' | 'create' | 'unlock' | 'creating' | 'logging_in'
  | 'subscription_required' | 'geofenced' | 'age_verification_required' | 'age_verification_failed' | 'age_verification_cooldown';

export function IdentityModal({ isOpen, onClose, unlockMode = false, onMinimize }: IdentityModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { info: toastInfo } = useToast();
  const { apiBaseUrl } = useAppConfig();
  const platformCapabilities = usePlatformCapabilities();
  const panicApi = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);
  const { createIdentity, loginToIdentity, unlockIdentity, logoutFromIdentity, hasIdentity, canCreateMore } = useIdentity();
  const { session } = useAuth();

  // Derive initial view from subscription state and alias gate
  const aliasGate = session?.aliasGate;
  const hasPaidPlan = session?.subscriptions && session.subscriptions.length > 0;
  const initialView = (): ModalView => {
    if (!hasPaidPlan) return 'subscription_required';
    if (aliasGate && !aliasGate.allowed) {
      if (aliasGate.code === 'GEOFENCE_BLOCKED') return 'geofenced';
      if (aliasGate.code === 'AGE_VERIFICATION_FAILED') return 'age_verification_failed';
      if (aliasGate.code === 'AGE_VERIFICATION_COOLDOWN') return 'age_verification_cooldown';
      if (aliasGate.code === 'AGE_VERIFICATION_REQUIRED') return 'age_verification_required';
    }
    return unlockMode ? 'unlock' : hasIdentity && !canCreateMore ? 'login' : 'choose';
  };

  const [view, setView] = useState<ModalView>(initialView);

  // Age verification
  const av = useAgeVerification();

  // Redirect to standalone age verification page when modal opens with AV required
  useEffect(() => {
    if (isOpen && view === 'age_verification_required') {
      onClose();
      navigate('/account/age-verification');
    }
  }, [isOpen, view, onClose, navigate]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Switch to unlock view when unlockMode prop changes
  useEffect(() => {
    if (unlockMode && isOpen) {
      setView('unlock');
    }
  }, [unlockMode, isOpen]);

  // Re-derive compliance view when the modal opens (handles close + reopen)
  useEffect(() => {
    if (isOpen) {
      if (!hasPaidPlan) {
        setView('subscription_required');
      } else if (aliasGate && !aliasGate.allowed) {
        if (aliasGate.code === 'GEOFENCE_BLOCKED') setView('geofenced');
        else if (aliasGate.code === 'AGE_VERIFICATION_FAILED') setView('age_verification_failed');
        else if (aliasGate.code === 'AGE_VERIFICATION_COOLDOWN') setView('age_verification_cooldown');
        else if (aliasGate.code === 'AGE_VERIFICATION_REQUIRED') setView('age_verification_required');
      }
    }
  }, [isOpen, hasPaidPlan, aliasGate]);

  // Sync view when aliasGate or subscription state changes (e.g. after AV approval or subscription activation refreshes session).
  // Skip auto-transition while the approved CTA is visible so the user can
  // read the success message and choose their next action.
  useEffect(() => {
    if (view === 'subscription_required' && hasPaidPlan) {
      if (aliasGate && !aliasGate.allowed) {
        if (aliasGate.code === 'GEOFENCE_BLOCKED') setView('geofenced');
        else if (aliasGate.code === 'AGE_VERIFICATION_FAILED') setView('age_verification_failed');
        else if (aliasGate.code === 'AGE_VERIFICATION_COOLDOWN') setView('age_verification_cooldown');
        else if (aliasGate.code === 'AGE_VERIFICATION_REQUIRED') setView('age_verification_required');
        else setView(unlockMode ? 'unlock' : hasIdentity && !canCreateMore ? 'login' : 'choose');
      } else {
        setView(unlockMode ? 'unlock' : hasIdentity && !canCreateMore ? 'login' : 'choose');
      }
    } else if (!aliasGate || aliasGate.allowed) {
      if ((view.startsWith('age_verification') || view === 'geofenced') && av.status !== 'approved') {
        av.cancel();
        setView(unlockMode ? 'unlock' : hasIdentity && !canCreateMore ? 'login' : 'choose');
      }
    }
  }, [aliasGate, av, canCreateMore, hasIdentity, hasPaidPlan, unlockMode, view]);

  // Never carry a reveal across views; leaving a password field should re-hide it.
  useEffect(() => {
    if (view !== 'login' && view !== 'unlock' && view !== 'create') {
      setPasswordVisible(false);
    }
  }, [view]);

  // Login/unlock passphrase. Create has its own fields inside the wizard.
  const [passphrase, setPassphrase] = useState('');

  // Owned here rather than inside CreateAliasWizard so a failed create can
  // return the user to the password step with their earlier answers intact.
  const wizard = useCreateAliasWizard();

  // Rate limiting info
  const [_retryAfter, setRetryAfter] = useState<number | null>(null);
  const [attemptNumber, setAttemptNumber] = useState<number | null>(null);

  // Login status for progress display
  const [loginStatus, setLoginStatus] = useState<LoginStatus>('authenticating');

  const loginInputRef = useRef<HTMLInputElement>(null);
  const unlockInputRef = useRef<HTMLInputElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Focus a password input after any CSS animation on the modal container
   * finishes. Focusing mid-animation (especially `transform`) can produce
   * "phantom focus" in Chromium/Electron where the caret blinks but
   * keystrokes are silently dropped. We listen for `animationend` on the
   * container so the timing is deterministic rather than a setTimeout guess.
   */
  const focusPasswordInput = useCallback((inputRef: RefObject<HTMLInputElement | null>) => {
    const el = inputRef.current;
    if (!el) return () => {};

    const container = modalContainerRef.current;
    let cleaned = false;

    const commitFocus = () => {
      if (cleaned) return;
      cleaned = true;
      if (container) container.removeEventListener('animationend', onAnimEnd);
      clearTimeout(fallbackTimer);
      el.blur();
      el.focus({ preventScroll: true });
    };

    const onAnimEnd = (e: AnimationEvent) => {
      if (e.target === container) commitFocus();
    };

    if (container && container.getAnimations().length > 0) {
      container.addEventListener('animationend', onAnimEnd);
    } else {
      el.focus({ preventScroll: true });
      return () => {};
    }

    const fallbackTimer = window.setTimeout(commitFocus, 500);

    return () => {
      cleaned = true;
      if (container) container.removeEventListener('animationend', onAnimEnd);
      clearTimeout(fallbackTimer);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || view !== 'unlock' || loading) return;
    return focusPasswordInput(unlockInputRef);
  }, [isOpen, view, loading, error, focusPasswordInput]);

  useLayoutEffect(() => {
    if (!isOpen || view !== 'login' || loading) return;
    return focusPasswordInput(loginInputRef);
  }, [isOpen, view, loading, error, focusPasswordInput]);

  const [passwordVisible, setPasswordVisible] = useState(false);

  /** When true, `handleClose` may dismiss the unlock overlay (success paths only). */
  const allowUnlockDismissRef = useRef(false);
  const [panicConfirmOpen, setPanicConfirmOpen] = useState(false);
  const [panicLoading, setPanicLoading] = useState(false);

  // Web device choice modal state
  const [webDeviceChoiceOpen, setWebDeviceChoiceOpen] = useState(false);
  const webDeviceChoiceResolver = useRef<((choice: WebDeviceChoice) => void) | null>(null);

  const handleWebDeviceChoice = useCallback((): Promise<WebDeviceChoice> => {
    return new Promise<WebDeviceChoice>((resolve) => {
      webDeviceChoiceResolver.current = resolve;
      setWebDeviceChoiceOpen(true);
    });
  }, []);

  const onWebDeviceChoiceSelected = useCallback((choice: WebDeviceChoice) => {
    setWebDeviceChoiceOpen(false);
    webDeviceChoiceResolver.current?.(choice);
    webDeviceChoiceResolver.current = null;
  }, []);

  // Remote passphrase-change migration prompt state
  const [migrationPromptOpen, setMigrationPromptOpen] = useState(false);
  const [migrationProcessing, setMigrationProcessing] = useState(false);
  const [migrationError, setMigrationError] = useState<'wrong-passphrase' | 'failed' | undefined>(undefined);
  const migrationResolver = useRef<((r: MigrationPromptResult) => void) | null>(null);

  const handleMigrationPrompt = useCallback((ctx: MigrationPromptContext): Promise<MigrationPromptResult> => {
    return new Promise<MigrationPromptResult>((resolve) => {
      migrationResolver.current = resolve;
      setMigrationError(ctx.lastError);
      setMigrationProcessing(false);
      setMigrationPromptOpen(true);
    });
  }, []);

  const onMigrationMigrate = useCallback((oldPassphrase: string) => {
    // Keep the modal open with a spinner; useIdentity will either complete the
    // login/unlock (which closes the modal) or re-invoke the prompt on failure.
    setMigrationProcessing(true);
    migrationResolver.current?.({ action: 'migrate', oldPassphrase });
    migrationResolver.current = null;
  }, []);

  const onMigrationOptOut = useCallback(() => {
    setMigrationPromptOpen(false);
    setMigrationProcessing(false);
    migrationResolver.current?.({ action: 'skip' });
    migrationResolver.current = null;
  }, []);

  const closeMigrationPrompt = useCallback(() => {
    setMigrationPromptOpen(false);
    setMigrationProcessing(false);
    setMigrationError(undefined);
    migrationResolver.current = null;
  }, []);

  const resetForm = () => {
    setPassphrase('');
    wizard.reset();
    setError(null);
    setSuccess(null);
    setRetryAfter(null);
    setAttemptNumber(null);
    setPasswordVisible(false);
  };

  const handleClose = () => {
    // Web device choice renders in a portal; clicks can still hit this overlay.
    // Never dismiss the identity shell until the user confirms that flow.
    if (webDeviceChoiceOpen) return;
    // Likewise keep the shell while a passphrase-migration prompt is in flight.
    if (migrationPromptOpen) return;
    if (view === 'unlock' && !allowUnlockDismissRef.current) return;

    av.cancel();
    resetForm();
    setView(unlockMode ? 'unlock' : hasIdentity && !canCreateMore ? 'login' : 'choose');
    onClose();
    allowUnlockDismissRef.current = false;
  };

  const handleLogin = async () => {
    if (loading) return; // Prevent multiple submissions
    if (passphrase.length < 8) {
      setError(t('identity.create.passwordHint'));
      return;
    }

    const passphraseValue = passphrase;
    
    // Switch to logging_in view immediately
    setView('logging_in');
    setLoginStatus('authenticating');
    setLoading(true);
    setError(null);
    setPassphrase('');

    const result = await loginToIdentity(passphraseValue, {
      onStatusChange: (status) => setLoginStatus(status),
      onWebDeviceChoice: handleWebDeviceChoice,
      onMigrationPrompt: handleMigrationPrompt,
    });

    setLoading(false);
    closeMigrationPrompt();

    if (result.success) {
      setSuccess(t('identity.login.success'));

      // Show toast for new device registration
      if (result.isNewDevice) {
        const deviceName = result.deviceName ?? 'this device';
        toastInfo(
          t('identity.device.newDeviceTitle'),
          t('identity.device.newDeviceMessage', { deviceName })
        );
      }

      setTimeout(() => {
        handleClose();
      }, 500);
    } else {
      // Go back to login view to show error (not choose — user was submitting login)
      setView('login');
      if (result.errorCode === 'RATE_LIMITED') {
        const seconds = result.retryAfter ?? 0;
        setError(t('identity.login.errorRateLimited', { seconds }));
        if (result.retryAfter) setRetryAfter(result.retryAfter);
      } else {
        setError(t(loginIdentityErrorI18nKey(result.errorCode)));
      }
      setAttemptNumber(result.attemptNumber ?? null);
    }
  };

  const handleUnlock = async () => {
    if (loading) return;
    if (passphrase.length < 8) {
      setError(t('identity.unlock.passwordRequired'));
      return;
    }

    const passphraseValue = passphrase;
    setLoading(true);
    setError(null);
    setPassphrase('');

    const result = await unlockIdentity(passphraseValue, {
      onMigrationPrompt: handleMigrationPrompt,
    });

    setLoading(false);
    closeMigrationPrompt();

    if (result.success) {
      setSuccess(t('identity.unlock.success'));
      allowUnlockDismissRef.current = true;
      setTimeout(() => {
        handleClose();
      }, 500);
    } else {
      setError(t(unlockIdentityErrorI18nKey(result.errorCode)));
    }
  };

  /** Must await server logout before closing; otherwise locked UI can reopen until the cookie clears. */
  const handleFullyLogout = async () => {
    setLoading(true);
    allowUnlockDismissRef.current = true;
    try {
      await logoutFromIdentity();
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAndExit = async () => {
    setLoading(true);
    allowUnlockDismissRef.current = true;
    try {
      await logoutFromIdentity();
      if (platformCapabilities.exitApplication) {
        await platformCapabilities.exitApplication();
      }
    } catch (err) {
      console.error('[IdentityModal] logout and exit failed', err);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handlePanicConfirm = async () => {
    setPanicLoading(true);
    allowUnlockDismissRef.current = true;
    try {
      await panicApi.auth.logout().catch(() => {});
      await panicWipeLocalClientData(platformCapabilities);
      if (platformCapabilities.exitApplication) {
        await platformCapabilities.exitApplication();
      }
    } catch (err) {
      console.error('[IdentityModal] panic wipe failed', err);
    } finally {
      setPanicLoading(false);
      setPanicConfirmOpen(false);
      handleClose();
    }
  };

  const handleCreate = async () => {
    if (loading) return; // Prevent multiple submissions

    const { passphrase: passphraseValue, passphraseConfirm, username, displayName } = wizard;
    if (passphraseValue.length < MIN_PASSPHRASE_LENGTH) {
      setError(t('identity.create.passwordHint'));
      return;
    }
    if (passphraseValue !== passphraseConfirm) {
      setError(t('identity.create.passwordMismatch'));
      return;
    }

    // Switch to creating view immediately (hides form, shows loader)
    setView('creating');
    setLoading(true);
    setError(null);
    wizard.setPassphrase('');
    wizard.setPassphraseConfirm('');

    const result = await createIdentity(
      passphraseValue,
      username,
      displayName,
      wizard.displayNamePrivacy,
    );

    setLoading(false);

    if (result.success) {
      setSuccess(t('identity.create.success'));
      setTimeout(() => {
        resetForm();
        setView('login');
        setSuccess(null);
      }, 1500);
    } else {
      // Back to the wizard, which still holds the username and display name.
      setView('create');
      setError(t(createIdentityErrorI18nKey(result.errorCode)));
    }
  };

  const passwordVisibilityToggle = (
    <button
      type="button"
      className="input-password-toggle"
      onClick={() => setPasswordVisible((v) => !v)}
      disabled={loading}
      aria-label={
        passwordVisible
          ? t('identity.passwordVisibility.hide')
          : t('identity.passwordVisibility.show')
      }
      aria-pressed={passwordVisible}
    >
      <Icon name={passwordVisible ? 'eyeSlash' : 'eye'} />
    </button>
  );

  const isNonDismissable = view === 'unlock' || view === 'creating' || view === 'logging_in';

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => { if (!details.open) handleClose(); }}
      closeOnInteractOutside={!isNonDismissable && !webDeviceChoiceOpen && !migrationPromptOpen}
      closeOnEscape={!isNonDismissable && !webDeviceChoiceOpen && !migrationPromptOpen}
      trapFocus
      initialFocusEl={
        view === 'unlock' ? () => unlockInputRef.current
        : view === 'login' ? () => loginInputRef.current
        : undefined
      }
      preventScroll
      lazyMount
    >
      <Portal>
        <Dialog.Backdrop className="confirm-dialog-backdrop identity-modal-backdrop" />
        <Dialog.Positioner className="confirm-dialog-positioner identity-modal-positioner">
          <Dialog.Content ref={modalContainerRef} className="identity-modal" aria-label={t('identity.title')}>
            {!isNonDismissable && (
              <Dialog.CloseTrigger className="modal-close" aria-label={t('common.close', 'Close')}>
                &times;
              </Dialog.CloseTrigger>
            )}
            {isNonDismissable && view === 'unlock' && onMinimize && (
              <button
                type="button"
                className="modal-close"
                aria-label={t('identity.unlock.minimize')}
                disabled={loading || panicLoading}
                onClick={onMinimize}
              >
                &times;
              </button>
            )}

            {view === 'choose' && (
              <div className="identity-modal-content">
                <div className="identity-modal-header">
                  <Icon name="mask" className="identity-modal-icon" />
                  <Dialog.Title asChild><h2>{t('identity.title')}</h2></Dialog.Title>
                  <Dialog.Description asChild><p>{t('identity.create.subtitle')}</p></Dialog.Description>
                </div>

                <div className="identity-modal-actions">
                  {hasIdentity && (
                    <Button variant="primary" size="lg" onClick={() => setView('login')}>
                      <Icon name="lock" />
                      {t('identity.loginToExistingButton')}
                    </Button>
                  )}

                  {canCreateMore && (
                    <Button
                      variant={hasIdentity ? 'secondary' : 'primary'}
                      size="lg"
                      onClick={() => setView('create')}
                    >
                      <Icon name="plus" />
                      {t('identity.createButton')}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {view === 'login' && (
              <div className="identity-modal-content">
                <div className="identity-modal-header">
                  <Icon name="mask" className="identity-modal-icon" />
                  <Dialog.Title asChild><h2>{t('identity.login.title')}</h2></Dialog.Title>
                  <Dialog.Description asChild><p>{t('identity.login.subtitle')}</p></Dialog.Description>
                </div>

                {error && <Alert variant="error">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <form
                  className="identity-modal-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleLogin();
                  }}
                >
                  <Input
                    ref={loginInputRef}
                    type={passwordVisible ? 'text' : 'password'}
                    placeholder={t('identity.login.passwordPlaceholder')}
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    disabled={loading}
                    autoComplete="current-password"
                    rightIcon={passwordVisibilityToggle}
                  />

                  {attemptNumber && attemptNumber >= 3 && (
                    <p className="identity-attempt-warning">
                      {t('identity.login.attemptsRemaining', { remaining: 6 - attemptNumber })}
                    </p>
                  )}

                  <div className="identity-modal-buttons">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || passphrase.length < 8}
                    >
                      {loading ? <Spinner size="sm" /> : t('identity.login.submitButton')}
                    </Button>
                  </div>
                </form>

                <div className="identity-modal-footer">
                  {canCreateMore && (
                    !hasIdentity ? (
                      <>
                        <p>{t('identity.login.noIdentity')}</p>
                        <Button variant="ghost" size="sm" onClick={() => setView('create')}>
                          {t('identity.login.createPrompt')}
                        </Button>
                      </>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => setView('create')}>
                        {t('identity.login.createAnotherPrompt')}
                      </Button>
                    )
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { onClose(); navigate('/account/passphrase'); }}
                  >
                    {t('identity.login.changePasswordLink')}
                  </Button>
                </div>
              </div>
            )}

            {view === 'unlock' && (
              <div className="identity-modal-content">
                <UpdateAvailableBanner variant="compact" />
                <div className="identity-modal-header">
                  <Icon name="lock" className="identity-modal-icon" />
                  <Dialog.Title asChild><h2>{t('identity.unlock.title')}</h2></Dialog.Title>
                  <Dialog.Description asChild><p>{t('identity.unlock.subtitle')}</p></Dialog.Description>
                </div>

                {error && <Alert variant="error">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <form
                  className="identity-modal-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleUnlock();
                  }}
                >
                  <Input
                    ref={unlockInputRef}
                    type={passwordVisible ? 'text' : 'password'}
                    placeholder={t('identity.unlock.passwordPlaceholder')}
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    disabled={loading}
                    autoComplete="current-password"
                    rightIcon={passwordVisibilityToggle}
                  />

                  <div className="identity-modal-buttons">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || passphrase.length < 8}
                    >
                      {loading ? <Spinner size="sm" /> : t('identity.unlock.submitButton')}
                    </Button>
                  </div>
                </form>

                <div className="identity-modal-footer identity-modal-footer-unlock">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={loading || panicLoading}
                    onClick={() => void handleFullyLogout()}
                  >
                    {loading ? <Spinner size="sm" /> : t('identity.unlock.logoutButton')}
                  </Button>
                  {platformCapabilities.exitApplication && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={loading || panicLoading}
                      onClick={() => void handleLogoutAndExit()}
                    >
                      {loading ? <Spinner size="sm" /> : t('identity.unlock.logoutAndExitButton')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={loading || panicLoading}
                    className="identity-modal-panic-btn"
                    onClick={() => setPanicConfirmOpen(true)}
                  >
                    {t('identity.unlock.panicButton')}
                  </Button>
                </div>
              </div>
            )}

            {view === 'create' && (
              <div className="identity-modal-content">
                <div className="identity-modal-header">
                  <Icon name="mask" className="identity-modal-icon" />
                  <Dialog.Title asChild><h2>{t('identity.create.title')}</h2></Dialog.Title>
                  <Dialog.Description asChild><p>{t('identity.create.subtitle')}</p></Dialog.Description>
                </div>

                {error && <Alert variant="error">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <CreateAliasWizard
                  wizard={wizard}
                  passwordVisible={passwordVisible}
                  onPasswordVisibleChange={setPasswordVisible}
                  onSubmit={() => void handleCreate()}
                  onCancel={() => setView('choose')}
                  submitting={loading}
                />
              </div>
            )}

            {view === 'creating' && (
              <div className="identity-modal-content identity-modal-creating">
                <div className="identity-creating-loader">
                  <Spinner size="lg" />
                  <h2>{success ? t('identity.create.success') : t('identity.create.creatingTitle')}</h2>
                  <p>{success ? t('identity.create.redirecting') : t('identity.create.creatingSubtitle')}</p>
                </div>
              </div>
            )}

            {view === 'logging_in' && (
              <div className="identity-modal-content identity-modal-creating">
                <div className="identity-creating-loader">
                  <Spinner size="lg" />
                  <h2>{success ? t('identity.login.success') : t('identity.login.loggingInTitle')}</h2>
                  <p>{success ? t('identity.login.redirecting') : t(`identity.login.status.${loginStatus}`)}</p>
                </div>
              </div>
            )}

            {view === 'subscription_required' && (
              <div className="identity-modal-content">
                <div className="identity-modal-header">
                  <Icon name="lock" className="identity-modal-icon" />
                  <Dialog.Title asChild><h2>{t('compliance.subscription.title')}</h2></Dialog.Title>
                </div>
                <p className="identity-modal-av-description">
                  {t('compliance.subscription.description')}
                </p>
                <div className="identity-modal-actions">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => { onClose(); navigate('/account/subscription'); }}
                  >
                    {t('compliance.subscription.subscribeCta')}
                  </Button>
                </div>
              </div>
            )}

            {view === 'geofenced' && (
              <div className="identity-modal-content">
                <div className="identity-modal-header">
                  <Icon name="lock" className="identity-modal-icon" />
                  <Dialog.Title asChild><h2>{t('compliance.geofence.title')}</h2></Dialog.Title>
                </div>
                <Alert variant="error">
                  {t('compliance.geofence.description')}
                </Alert>
                {aliasGate?.jurisdiction && (
                  <p className="identity-modal-jurisdiction">
                    {t('compliance.geofence.jurisdictionLabel')}: <strong>{aliasGate.jurisdiction}</strong>
                  </p>
                )}
                {aliasGate?.lawUrl && (
                  <a
                    href={aliasGate.lawUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="identity-modal-law-link"
                  >
                    {t('compliance.geofence.viewLaw')}
                  </a>
                )}
              </div>
            )}

            {/* age_verification_required redirects to /account/age-verification via useEffect */}

            {view === 'age_verification_failed' && (
              <div className="identity-modal-content">
                <div className="identity-modal-header">
                  <Icon name="lock" className="identity-modal-icon" />
                  <Dialog.Title asChild><h2>{t('compliance.ageVerification.title')}</h2></Dialog.Title>
                </div>
                <Alert variant="error">
                  {t('compliance.ageVerification.failedMessage')}
                </Alert>
                {aliasGate?.retryAfter && (
                  <p className="identity-modal-retry-after">
                    {t('compliance.ageVerification.retryAfterLabel')}: {new Date(aliasGate.retryAfter).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}

            {view === 'age_verification_cooldown' && (
              <div className="identity-modal-content">
                <div className="identity-modal-header">
                  <Icon name="lock" className="identity-modal-icon" />
                  <Dialog.Title asChild><h2>{t('compliance.ageVerification.title')}</h2></Dialog.Title>
                </div>
                <Alert variant="warning">
                  {t('compliance.ageVerification.cooldownMessage')}
                </Alert>
                {aliasGate?.retryAfter && (
                  <p className="identity-modal-retry-after">
                    {t('compliance.ageVerification.retryAfterLabel')}: {new Date(aliasGate.retryAfter).toLocaleDateString()}
                  </p>
                )}
                {session?.ageVerification?.expirationCount !== undefined && (
                  <p className="identity-modal-expiration-count">
                    {t('compliance.ageVerification.expirationCount', {
                      count: session.ageVerification.expirationCount,
                      max: 3,
                    })}
                  </p>
                )}
              </div>
            )}

            {/* Jurisdiction advisory banner for unresolved geo */}
            {(view === 'choose' || view === 'login' || view === 'create') &&
              session?.ageVerification === undefined &&
              !session?.geo?.jurisdiction && (
              <div className="identity-modal-advisory">
                <Alert variant="info">
                  {t('compliance.advisory.unresolvedJurisdiction')}
                </Alert>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => { onClose(); navigate('/account/age-verification?optIn=1'); }}
                >
                  {t('compliance.advisory.optInButton')}
                </Button>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>

      <WebDeviceChoiceModal
        open={webDeviceChoiceOpen}
        onChoice={onWebDeviceChoiceSelected}
      />

      <MigrationPromptModal
        open={migrationPromptOpen}
        processing={migrationProcessing}
        lastError={migrationError}
        onMigrate={onMigrationMigrate}
        onOptOut={onMigrationOptOut}
      />

      <ConfirmDialog
        open={panicConfirmOpen}
        onOpenChange={setPanicConfirmOpen}
        title={t('identity.unlock.panicConfirmTitle')}
        description={t('identity.unlock.panicConfirmBody')}
        confirmLabel={t('identity.unlock.panicConfirmAction')}
        cancelLabel={t('common.cancel', 'Cancel')}
        variant="danger"
        loading={panicLoading}
        closeOnInteractOutside={!panicLoading}
        onConfirm={() => void handlePanicConfirm()}
      />
    </Dialog.Root>
  );
}
